import { Component, Input, OnInit } from '@angular/core';
import { StammdatenService } from '../../stammdaten.service';
import { MessageService } from '../../../../services/message-service/message.service';
import { Tierreklamation } from '../../../gruppe-tier/classes/tierreklamation';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../../../dialogs/confirm-dialog/confirm-dialog.component';
import { Subscription } from 'rxjs';
import { ConfirmDialogInput, ConfirmDialogResult } from '../../../dialogs/confirm-dialog/confirm-dialog';
import { EnterValueDialogComponent } from '../../../dialogs/enter-value-dialog/enter-value-dialog.component';
import { EnterValueInput, EnterValueResponse } from '../../../dialogs/enter-value-dialog/enter-value-response';
import { LocalStorageService } from '../../../../services/local-storage/local-storage.service';

@Component({
    selector: 'kiene-tierreklamationen-uebersicht',
    templateUrl: './tier-reklamationen-uebersicht.component.html',
    styleUrls: ['./tier-reklamationen-uebersicht.component.scss'],
})
export class TierReklamationenUebersichtComponent implements OnInit {
    @Input('showReturnButton') showReturnButton = true;
    tierreklamationen: Tierreklamation[] = [];
    sortDirection: 'asc' | 'desc' = 'asc';
    isLoading = true;
    isSaving = false;
    canEditTierreklamationen = false;
    lastAddedReklamationText: string | null = null;
    loadTierreklamationenSub: Subscription;

    constructor(
        private readonly stammdatenService: StammdatenService,
        private readonly messageService: MessageService,
        private readonly dialog: MatDialog,
        public localStorageService: LocalStorageService,
    ) {}

    ngOnInit(): void {
        this.canEditTierreklamationen = this.localStorageService.hasPermission(9232);
        this.loadTierreklamationen();
    }

    toggleSortBeschreibung(): void {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    }

    loadTierreklamationen(): void {
        this.isLoading = true;
        this.loadTierreklamationenSub?.unsubscribe();
        this.loadTierreklamationenSub = this.stammdatenService.getTierreklamationen(false).subscribe({
            next: tierreklamationen => {
                this.isLoading = false;
                this.tierreklamationen = tierreklamationen.records;
                if (this.lastAddedReklamationText) {
                    const idx = this.tierreklamationen.findIndex(
                        t => (t?.reklamation ?? '').trim() === this.lastAddedReklamationText,
                    );
                    if (idx > 0) {
                        const [added] = this.tierreklamationen.splice(idx, 1);
                        this.tierreklamationen.unshift(added);
                    }
                    this.lastAddedReklamationText = null;
                }
            },
            error: err => {
                this.isLoading = false;
                this.messageService.errorMessage('Fehler beim Laden der Tierreklamationen', err);
            },
        });
    }

    deleteTierreklamation(event: Event, tierreklamation: Tierreklamation) {
        if (!this.localStorageService.hasPermission(9234)) {
            return;
        }
        event.stopPropagation();
        const input = new ConfirmDialogInput();
        input.headline = 'Tierreklamation löschen';
        input.text = 'Soll der Tierreklamation wirklich gelöscht werden?';
        input.okButton = 'Löschen';

        const dialogRef = this.dialog.open(ConfirmDialogComponent, { width: '100%', maxWidth: '400px', data: input });

        dialogRef.afterClosed().subscribe({
            next: (result: ConfirmDialogResult) => {
                if (result?.confirmed) {
                    this.stammdatenService.deleteTierreklamation(tierreklamation).subscribe({
                        next: () => {
                            this.loadTierreklamationen();
                        },
                        error: err => {
                            this.messageService.errorMessage('Fehler beim Löschen des Tierreklamations', err);
                        },
                    });
                }
            },
        });
    }

    editTierreklamation(tierreklamation: Tierreklamation) {
        if (!this.canEditTierreklamationen) {
            return;
        }
        this.reklamationAnlegen(tierreklamation);
    }

    zugriffFreigeben() {
        if (!this.canEditTierreklamationen) {
            return;
        }
        this.stammdatenService.reklamationenTeilen();
    }

    reklamationAnlegen(tierreklamation?: Tierreklamation) {
        if (!this.canEditTierreklamationen) {
            return;
        }
        const inp = new EnterValueInput();
        inp.title = 'Tierreklamation ' + (tierreklamation ? 'bearbeiten' : 'anlegen');
        inp.addInputValue('Bezeichnung', 'text', tierreklamation?.reklamation);
        this.dialog
            .open(EnterValueDialogComponent, { data: inp })
            .afterClosed()
            .subscribe({
                next: (result: EnterValueResponse) => {
                    if (result) {
                        if (tierreklamation) {
                            tierreklamation.reklamation = result.getValue(0);
                            this.stammdatenService.updateTierreklamation(tierreklamation).subscribe({
                                next: () => {
                                    this.loadTierreklamationen();
                                },
                                error: err => {
                                    this.messageService.errorMessage('Fehler!', err);
                                },
                            });
                        } else {
                            const createdText = result.getValue(0).trim();
                            this.lastAddedReklamationText = createdText;
                            this.stammdatenService.addTierreklamation(createdText).subscribe({
                                next: () => {
                                    this.loadTierreklamationen();
                                },
                                error: err => {
                                    this.messageService.errorMessage(
                                        'Fehler beim Hinzufügen des Tierreklamations',
                                        err,
                                    );
                                },
                            });
                        }
                    }
                },
            });
    }

    getSortedTierreklamationen(): Tierreklamation[] {
        const direction = this.sortDirection === 'asc' ? 1 : -1;
        return [...(this.tierreklamationen ?? [])].sort((a: Tierreklamation, b: Tierreklamation) => {
            const aValue = (a?.reklamation ?? '').toLowerCase();
            const bValue = (b?.reklamation ?? '').toLowerCase();
            return aValue.localeCompare(bValue, 'de') * direction;
        });
    }

    goBack(): void {
        window.history.back();
    }
}
